import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { parseApiError } from '@/api/parse-error';
import { addBreadcrumb } from '@/shared/lib/breadcrumbs';
import { captureMutationError } from '@/shared/lib/sentry-utils';
import { queryKeys, POLLING_INTERVALS } from '../query-keys';
import {
  getSystemHealth,
  getSystemMetrics,
  getExchangesHealth,
  getDataQuality,
  getSystemRisk,
  getConfirmationToken,
  systemHalt,
  systemResume,
  getExchanges,
  getExchangesMaintenance,
  getKillSwitchStatus,
  activateKillSwitch,
  deactivateKillSwitch,
  getExchangeCBList,
} from '../endpoints/system';

export function useSystemHealth() {
  return useQuery({
    queryKey: queryKeys.system.health(),
    queryFn: getSystemHealth,
    refetchInterval: POLLING_INTERVALS.systemHealth,
    refetchIntervalInBackground: false,
  });
}

export function useSystemMetrics() {
  return useQuery({
    queryKey: queryKeys.system.metrics(),
    queryFn: getSystemMetrics,
    refetchInterval: POLLING_INTERVALS.systemMetrics,
    refetchIntervalInBackground: false,
  });
}

export function useExchangesHealth() {
  return useQuery({
    queryKey: queryKeys.system.exchangesHealth(),
    queryFn: getExchangesHealth,
    refetchInterval: POLLING_INTERVALS.exchangesHealth,
    refetchIntervalInBackground: false,
  });
}

export function useDataQuality() {
  return useQuery({
    queryKey: queryKeys.system.dataQuality(),
    queryFn: getDataQuality,
    refetchInterval: POLLING_INTERVALS.dataQuality,
    refetchIntervalInBackground: false,
  });
}

export function useSystemRisk() {
  return useQuery({
    queryKey: queryKeys.system.risk(),
    queryFn: getSystemRisk,
    refetchInterval: POLLING_INTERVALS.systemRisk,
    refetchIntervalInBackground: false,
  });
}

/** Fetch a time-limited confirmation token for halt/resume actions */
export function useConfirmationToken() {
  return useMutation({
    mutationFn: (scope: string) => getConfirmationToken(scope),
  });
}

export function useSystemHalt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => systemHalt(reason),
    onSuccess: () => {
      addBreadcrumb('system.action', 'System halted');
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.all() });
      toast.warning('System halted — all bots stopped');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to halt system'));
      captureMutationError(err, { mutation: 'system_halt' });
    },
  });
}

export function useSystemResume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => systemResume(token),
    onSuccess: () => {
      addBreadcrumb('system.action', 'System resumed');
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.all() });
      toast.success('System resumed');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to resume system'));
      captureMutationError(err, { mutation: 'system_resume' });
    },
  });
}

export function useExchanges() {
  return useQuery({
    queryKey: queryKeys.system.exchanges(),
    queryFn: getExchanges,
    staleTime: 30_000,
  });
}

export function useExchangesMaintenance() {
  return useQuery({
    queryKey: queryKeys.system.exchangesMaintenance(),
    queryFn: getExchangesMaintenance,
    refetchInterval: POLLING_INTERVALS.exchangesHealth,
    refetchIntervalInBackground: false,
  });
}

// ── Kill Switch ─────────────────────────────────────────────────────────────

export function useKillSwitchStatus() {
  return useQuery({
    queryKey: queryKeys.system.killSwitch(),
    queryFn: getKillSwitchStatus,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      // Don't retry if kill-switch service is not configured (503)
      if (error && typeof error === 'object' && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 503) return false;
      }
      return failureCount < 3;
    },
  });
}

export function useActivateKillSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => activateKillSwitch({ reason }),
    onSuccess: () => {
      addBreadcrumb('system.action', 'Kill switch activated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.killSwitch() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.all() });
      toast.warning('Kill switch activated — all trading halted');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to activate kill switch'));
      captureMutationError(err, { mutation: 'activate_kill_switch' });
    },
  });
}

export function useDeactivateKillSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => deactivateKillSwitch(token),
    onSuccess: () => {
      addBreadcrumb('system.action', 'Kill switch deactivated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.killSwitch() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.health() });
      toast.success('Kill switch deactivated');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to deactivate kill switch'));
      captureMutationError(err, { mutation: 'deactivate_kill_switch' });
    },
  });
}

// ── Exchange Circuit Breaker ────────────────────────────────────────────────

export function useExchangeCBList() {
  return useQuery({
    queryKey: queryKeys.system.exchangeCB(),
    queryFn: getExchangeCBList,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
